import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, switchMap } from 'rxjs';

/**
 * Délai de repli quand `requestIdleCallback` manque — WKWebView ne l'a qu'à
 * partir d'iOS 18.4, et la cible du projet descend bien plus bas.
 */
const IDLE_FALLBACK_MS = 2000;

/**
 * Au-delà, on précharge même si le fil principal n'a jamais été libre : une
 * application qui reste occupée ne doit pas se retrouver sans ses écrans hors
 * ligne pour autant.
 */
const IDLE_DEADLINE_MS = 10_000;

/** Émet une fois que le fil principal a rendu la main, puis se termine. */
function whenIdle(): Observable<void> {
  return new Observable<void>((subscriber) => {
    const done = (): void => {
      subscriber.next();
      subscriber.complete();
    };

    if (typeof requestIdleCallback !== 'function') {
      const timer = setTimeout(done, IDLE_FALLBACK_MS);
      return () => clearTimeout(timer);
    }

    const handle = requestIdleCallback(done, { timeout: IDLE_DEADLINE_MS });
    return () => cancelIdleCallback(handle);
  });
}

/**
 * Précharge toutes les routes, mais seulement une fois le fil principal libre.
 *
 * `PreloadAllModules` déclenche le chargement dès la fin du bootstrap. Dans une
 * application Capacitor les fichiers sont LOCAUX : leur téléchargement ne coûte
 * rien, mais leur ANALYSE, elle, se paie — près d'un mégaoctet de JavaScript
 * passait à la moulinette sur le fil qui devait peindre le premier écran.
 *
 * Le bénéfice du préchargement est intégralement conservé — l'application reste
 * utilisable hors ligne sur toutes ses routes —, il est simplement déplacé hors
 * de la fenêtre critique du démarrage.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloading implements PreloadingStrategy {
  preload(_route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return whenIdle().pipe(switchMap(() => load()));
  }
}
